import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/client';
import { Library, PaginatedResponse } from '../types';
import Spinner from '../components/Spinner';
import EmptyState from '../components/EmptyState';
import Badge from '../components/Badge';

const statusVariant: Record<string, 'success' | 'warning' | 'default'> = {
  active: 'success',
  needs_review: 'warning',
  skipped: 'default',
};

export default function LibraryList() {
  const [libraries, setLibraries] = useState<Library[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stateFilter, setStateFilter] = useState('');
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (stateFilter) params.set('state', stateFilter);
    params.set('page', String(page));
    params.set('per_page', '25');

    api.get<PaginatedResponse<Library>>(`/libraries?${params}`)
      .then((res) => {
        setLibraries(res.data);
        setLastPage(res.last_page);
        setTotal(res.total);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [stateFilter, page]);

  // Reset page when filter changes
  useEffect(() => { setPage(1); }, [stateFilter]);

  if (error) return <p className="error">{error}</p>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1>Libraries</h1>
        <div className="gap-row">
          <Link to="/libraries/new"><button>Add Library</button></Link>
          <Link to="/libraries/import"><button>Import CSV</button></Link>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', marginBottom: '1rem' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: 0 }}>
          State:
          <input
            type="text"
            maxLength={2}
            placeholder="e.g. CA"
            value={stateFilter}
            onChange={(e) => setStateFilter(e.target.value.toUpperCase())}
            style={{ width: '5rem' }}
          />
        </label>
        <span className="text-muted">{total} librarie{total !== 1 ? 's' : ''}</span>
      </div>

      {loading ? (
        <Spinner text="Loading libraries..." />
      ) : libraries.length === 0 ? (
        <EmptyState
          title="No libraries found"
          description={stateFilter ? `No libraries match state "${stateFilter}".` : "Add your first library to get started."}
        />
      ) : (
        <>
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>City</th>
                <th>State</th>
                <th>ILS Vendor</th>
                <th>Ingest</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {libraries.map((lib) => (
                <tr key={lib.id}>
                  <td><Link to={`/libraries/${lib.id}`}>{lib.name}</Link></td>
                  <td>{lib.city}</td>
                  <td>{lib.state}</td>
                  <td>{lib.ils_vendor || <span className="text-muted">—</span>}</td>
                  <td><Badge label={lib.ingest_method} /></td>
                  <td><Badge label={lib.status} variant={statusVariant[lib.status]} /></td>
                  <td><Link to={`/libraries/${lib.id}/edit`}>Edit</Link></td>
                </tr>
              ))}
            </tbody>
          </table>

          {lastPage > 1 && (
            <div className="pagination">
              <button disabled={page <= 1} onClick={() => setPage(page - 1)}>
                Previous
              </button>
              <span className="text-muted">
                Page {page} of {lastPage}
              </span>
              <button disabled={page >= lastPage} onClick={() => setPage(page + 1)}>
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
